import { labStrategyLevels, type LabFrame, type LabLevel, type LabReplay, type LabStrategy } from './alarmLab'

/** How the platform would raise a strategy's events, frame by frame.
 *
 *  The Lab's replay keeps its own event book: an event closes the moment the
 *  readings no longer meet the condition, and drops back from Alarm to
 *  Warning. A live strategy's events are the platform's, kept by the rule
 *  monitor's `events.reconcile` (backend/app/alerting/events.py) like every
 *  other rule's: an event opens at its confirmed level, only ever rises
 *  (Warning to Alarm, once), stays open while the readings keep it at a
 *  level, closes once it has been clear for 15 minutes, and an unknown frame
 *  neither opens nor closes it. This is that reconcile, for one rule, over
 *  the Lab's frames, so the Lab can show the events an engineer would get.
 *  The golden cases hold it to the server's (`platform` in
 *  backend/tests/fixtures/strategy/replays.json, tests/test_strategy.py). */

export const CLEAR_HOLD_MS = 15 * 60_000
const RANK: Record<LabLevel, number> = { warning: 1, alarm: 2 }

/** A frame as the rule monitor's verdict (alerting/strategy.py `verdict`). */
export interface FrameVerdict {
  /** The confirmed level: what opens and raises an event. */
  level: LabLevel | null
  /** What keeps an open event open. */
  keeps: LabLevel | null
  evaluated: boolean
}

/** The level the readings reach at a frame, before confirmation. */
export function truthLevel(s: LabStrategy, frame: Pick<LabFrame, 'trigger' | 'alarm'>): LabLevel | null {
  if (s.formula) return frame.trigger === true ? s.severity : null
  if (frame.alarm === true) return 'alarm'
  if (frame.trigger === true) return labStrategyLevels(s).includes('warning') ? 'warning' : 'alarm'
  return null
}

/** With sustained confirmation the confirmed level opens and raises an
 *  event and the readings' own level keeps it open, so unknown data that
 *  restarts the confirmation does not close an alarm the value is past. */
export function frameVerdict(s: LabStrategy, frame: LabFrame): FrameVerdict {
  if (frame.state === 'unknown') return { level: null, keeps: null, evaluated: false }
  const level = frame.state === 'active' ? frame.level : null
  return { level, keeps: s.confirm.mode === 'sustained' ? truthLevel(s, frame) : level, evaluated: true }
}

export interface PlatformEvent {
  number: number
  opened: number
  /** When it closed, or null while open at the period's end. */
  closed: number | null
  /** Its level, which only rises. */
  level: LabLevel
  escalated: number | null
}

/** What the platform shows at a frame: no event, an open one, or an open one
 *  waiting out its 15 clear minutes. */
export type PlatformState = 'none' | 'open' | 'clearing'

export function platformEvents(s: LabStrategy, replay: Pick<LabReplay, 'frames'>, holdMs = CLEAR_HOLD_MS):
    { events: PlatformEvent[]; states: PlatformState[] } {
  const events: PlatformEvent[] = []
  const states: PlatformState[] = []
  let open: PlatformEvent | null = null
  let lastSeen = 0
  for (const frame of replay.frames) {
    const v = frameVerdict(s, frame)
    if (!open) {
      if (v.evaluated && v.level) {
        open = { number: events.length + 1, opened: frame.time, closed: null, level: v.level, escalated: null }
        events.push(open)
        lastSeen = frame.time
      }
    } else if (v.evaluated) {
      if (v.level && RANK[v.level] > RANK[open.level]) {
        open.level = v.level; open.escalated = frame.time; lastSeen = frame.time
      } else if (v.keeps) {
        lastSeen = frame.time
      } else if (frame.time - lastSeen >= holdMs) {
        open.closed = frame.time
        open = null
      }
    }
    states.push(!open ? 'none' : frame.time === lastSeen ? 'open' : 'clearing')
  }
  return { events, states }
}
